import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { useProjectContext } from "../context/ProjectContext";
import { auditService, type GetAuditLogsParams } from "../services/audit";

export const auditLogsQueryKeys = {
	base: ["audit-logs"] as const,
	project: (projectId?: string, params?: GetAuditLogsParams) =>
		[...auditLogsQueryKeys.base, "project", projectId, params] as const,
	all: (params?: GetAuditLogsParams) =>
		[...auditLogsQueryKeys.base, "all", params] as const,
};

/**
 * Hook to fetch project audit logs
 */
export const useProjectAuditLogs = (
	params: GetAuditLogsParams = {},
	options?: { autoRefresh?: boolean; refreshInterval?: number },
) => {
	const { selectedProject } = useProjectContext();
	const projectId = selectedProject?.id;
	const { autoRefresh = false, refreshInterval = 30000 } = options || {};

	return useQuery({
		queryKey: auditLogsQueryKeys.project(projectId, params),
		queryFn: () => auditService.getProjectAuditLogs(projectId!, params),
		enabled: Boolean(projectId),
		staleTime: 1000 * 30, // 30 seconds
		refetchInterval: autoRefresh ? refreshInterval : false,
	});
};

/**
 * Hook to fetch all audit logs (superAdmin only)
 */
export const useAllAuditLogs = (
	params: GetAuditLogsParams = {},
	options?: { autoRefresh?: boolean; refreshInterval?: number },
) => {
	const { user } = useAuth();
	const isSuperAdmin = user?.role === "superAdmin";
	const { autoRefresh = false, refreshInterval = 30000 } = options || {};

	return useQuery({
		queryKey: auditLogsQueryKeys.all(params),
		queryFn: () => auditService.getAllAuditLogs(params),
		enabled: isSuperAdmin,
		staleTime: 1000 * 30, // 30 seconds
		refetchInterval: autoRefresh ? refreshInterval : false,
	});
};
